// Pure helpers for the AbsoluteSlider (#238). Kept out of the component so the
// fill maths, baseline-marker position, and value formatting are unit-testable
// without a DOM or React Testing Library. Mirrors delta_slider_logic.ts, but the
// fill emerges from the LEFT (min) rather than from the default — absolute level
// semantics, where the value itself matters, not the change.

import { formatEur, formatPercent } from "../../lib/formatters";

/** How an absolute slider formats its value readout, derived R-side from the
 *  parameter's `type`/`unit` and passed through the mount as `data-format`. */
export type AbsoluteFormat = "euro" | "percent" | "number";

/** The value's position along the track, in percent (0 at min, 100 at max).
 *  Used for both the fill width and the baseline-marker offset. */
export function trackPercent(min: number, max: number, value: number): number {
  const range = max - min;
  if (range <= 0) return 0;
  return ((value - min) / range) * 100;
}

/** The inline `background` for the range track: teal fill from the left (min)
 *  up to the current value, neutral beyond. Distinct from the delta bar, which
 *  fills from the default. Same two colours as the delta slider so the palette
 *  is shared (teal $beamm-control fill, neutral track). */
export function absoluteFillBackground(
  min: number,
  max: number,
  value: number,
): string {
  const pct = trackPercent(min, max, value);
  return `linear-gradient(
        to right,
        #1eb7ba 0%,
        #1eb7ba ${pct}%,
        #e0dce8 ${pct}%,
        #e0dce8 100%
      )`;
}

/** The baseline-marker offset (track percent) at the parameter default — the
 *  "modified-from-default visibly marked" UX principle. */
export function baselinePercent(
  min: number,
  max: number,
  defaultValue: number,
): number {
  return trackPercent(min, max, defaultValue);
}

/** Format an absolute value for the readout. Euro uses grouped thousands
 *  ("€9,050"); percent renders a fraction as a whole-or-trimmed percentage
 *  ("21%", "12.5%") — no trailing ".0" so it matches the figures users saw on
 *  the bslib sliders; number is the plain integer (counts). */
export function formatAbsolute(value: number, format: AbsoluteFormat): string {
  if (format === "euro") {
    return formatEur(value);
  }
  if (format === "percent") {
    // formatPercent gives one decimal ("21.0%"); trim a trailing ".0".
    return formatPercent(value).replace(/\.0%$/, "%");
  }
  return String(Math.round(value));
}
